import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { CustomField } from '../CustomFieldBuilder';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Slot, journeeEntiere, estJourneeEntiere } from './disponibilite';

/** Les jours d'un événement, du premier au dernier (ISO). */
export const eventDays = (start?: string | null, end?: string | null): string[] => {
  if (!start) return [];
  const d0 = new Date(`${start.slice(0, 10)}T12:00:00`);
  const d1 = new Date(`${(end || start).slice(0, 10)}T12:00:00`);
  if (isNaN(d0.getTime()) || isNaN(d1.getTime()) || d1 < d0) return [];
  const out: string[] = [];
  for (let d = d0; d <= d1 && out.length < 31; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

/**
 * Proposition de départ : une case par jour entier (« Toute la journée » sur un
 * événement d'une journée), à COCHER. L'organisateur définit ensuite ses
 * périodes (« samedi 8h–13h », « dès 9h », ou des créneaux d'une heure) et le
 * bénévole coche toutes celles où il est là. Même défaut que le serveur
 * (`routers/volunteers.py::_champ_dispo_defaut`).
 */
export const defaultAvailabilityField = (days: string[]): CustomField => {
  const slot_map: Record<string, Slot[]> = {};
  if (days.length === 1) {
    slot_map['Toute la journée'] = days.map(journeeEntiere);
  } else {
    days.forEach((d, i) => { slot_map[`Jour ${i + 1}`] = [journeeEntiere(d)]; });
  }
  return {
    id: 'availability',
    label: 'Quand es-tu disponible ? (coche tout ce qui te convient)',
    type: 'checkbox',
    required: true,
    scope: 'team',
    order: 1,
    options: Object.keys(slot_map),
    is_availability: true,
    slot_map,
  };
};

/** Un créneau horaire par défaut quand l'organisateur passe « aux heures ». */
const CRENEAU_HORAIRE_DEFAUT = { start: '08:00', end: '13:00' };

interface Props {
  field: CustomField;
  days: string[];
  onChange: (field: CustomField) => void;
}

/**
 * Éditeur de LA question de disponibilité.
 *
 * Différence avec le rôle : **les jours, on les connaît déjà** (dates de
 * l'événement). L'organisateur n'invente donc rien — il choisit seulement
 * comment il les REGROUPE, et depuis le 2026-09-26 s'il les découpe : chaque
 * jour coché est une journée entière, ou un créneau horaire (« samedi matin »).
 * Le planning des juges se fait à l'heure ; c'est cette question qui le nourrit.
 *
 * Un jour ne porte qu'UN créneau par proposition : « 08:00–13:00 puis
 * 15:00–18:00 » se dit en deux propositions, ou en cochant la journée. Tenir
 * plusieurs plages par jour dans une seule case rendrait la case illisible
 * pour le bénévole qui doit choisir.
 */
const AvailabilityQuestionEditor: React.FC<Props> = ({ field, days, onChange }) => {
  const { t, i18n } = useTranslation();
  const options = field.options || [];
  const slotMap = field.slot_map || {};

  const dayLabel = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(i18n.language, {
      weekday: 'short', day: 'numeric', month: 'short',
    });

  const creneauDe = (libelle: string, day: string): Slot | undefined =>
    (slotMap[libelle] || []).find((s) => s.date === day);

  const poser = (libelle: string, day: string, slot: Slot | null) => {
    const autres = (slotMap[libelle] || []).filter((s) => s.date !== day);
    const next = slot ? [...autres, slot] : autres;
    next.sort((a, b) => a.date.localeCompare(b.date) || a.start.localeCompare(b.start));
    onChange({ ...field, slot_map: { ...slotMap, [libelle]: next } });
  };

  const setOption = (index: number, libelle: string) => {
    const ancien = options[index];
    const next = [...options];
    next[index] = libelle;
    const map: Record<string, Slot[]> = {};
    Object.entries(slotMap).forEach(([k, v]) => { map[k === ancien ? libelle : k] = v; });
    onChange({ ...field, options: next, slot_map: map });
  };

  const addOption = () => onChange({
    ...field, options: [...options, ''], slot_map: { ...slotMap, '': [] },
  });

  const removeOption = (index: number) => {
    const libelle = options[index];
    const map = { ...slotMap };
    delete map[libelle];
    onChange({ ...field, options: options.filter((_, i) => i !== index), slot_map: map });
  };

  // Plusieurs cases (checkbox) : la disponibilité est la RÉUNION des périodes
  // cochées ; un seul choix (select) : une proposition porte tout.
  const multiple = field.type === 'checkbox';
  const setMultiple = (on: boolean) => onChange({ ...field, type: on ? 'checkbox' : 'select' });

  const incomplete = options.filter((o) => !o.trim() || !(slotMap[o] || []).length);
  const inverses = options.filter((o) =>
    (slotMap[o] || []).some((s) => s.start >= s.end));

  return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('volunteers.availabilityTitle')}</h3>
      <p className="text-sm text-muted-foreground mb-2">{t('volunteers.availabilityHelp')}</p>
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="avail-label">{t('volunteers.roleQuestionLabel')}</Label>
            <Input
              id="avail-label"
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={multiple} onCheckedChange={setMultiple} />
            <span>{t('volunteers.availabilityMultiple')}</span>
          </label>
          <p className="text-xs text-muted-foreground -mt-2">
            {multiple ? t('volunteers.availabilityMultipleHelp') : t('volunteers.availabilitySingleHelp')}
          </p>

          <div className="space-y-3">
            <Label>{t('volunteers.roleQuestionOptions')}</Label>
            {options.map((libelle, i) => (
              <div key={i} className="rounded-lg border p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={libelle}
                    placeholder={t('volunteers.availabilityOptionPlaceholder')}
                    onChange={(e) => setOption(i, e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    disabled={options.length <= 1}
                    onClick={() => removeOption(i)}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-1.5 pl-1">
                  {days.map((d) => {
                    const slot = creneauDe(libelle, d);
                    const horaire = !!slot && !estJourneeEntiere(slot);
                    return (
                      <div key={d} className="flex flex-wrap items-center gap-3 text-sm min-h-8">
                        <label className="flex items-center gap-1.5 cursor-pointer w-28">
                          <Checkbox
                            checked={!!slot}
                            onCheckedChange={(v) => poser(libelle, d, v ? journeeEntiere(d) : null)}
                          />
                          {dayLabel(d)}
                        </label>
                        {slot && (
                          <>
                            {/* Journée entière ou heures : un interrupteur, pas un
                                champ vide à interpréter. */}
                            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
                              <Switch
                                checked={horaire}
                                onCheckedChange={(on) => poser(libelle, d, on
                                  ? { date: d, ...CRENEAU_HORAIRE_DEFAUT }
                                  : journeeEntiere(d))}
                              />
                              {horaire ? t('volunteers.availabilityHours') : t('volunteers.availabilityAllDay')}
                            </label>
                            {horaire && (
                              <span className="flex items-center gap-1 tabular-nums">
                                <Input
                                  type="time"
                                  value={slot.start}
                                  className="h-8 w-[6.5rem]"
                                  onChange={(e) => e.target.value && poser(libelle, d, { ...slot, start: e.target.value })}
                                />
                                <span className="text-muted-foreground">→</span>
                                <Input
                                  type="time"
                                  value={slot.end === '24:00' ? '23:59' : slot.end}
                                  className="h-8 w-[6.5rem]"
                                  onChange={(e) => e.target.value && poser(libelle, d, { ...slot, end: e.target.value })}
                                />
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addOption}>
              <Plus className="h-4 w-4 mr-1" />
              {t('volunteers.roleQuestionAddOption')}
            </Button>
          </div>

          {incomplete.length > 0 && (
            <p className="text-xs text-warning">{t('volunteers.availabilityIncomplete')}</p>
          )}
          {inverses.length > 0 && (
            <p className="text-xs text-warning">{t('volunteers.availabilityInverted')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AvailabilityQuestionEditor;
